<template>
  <section class="rk-hero" >
    <div class="rk-hero__bg">
      <img
        ref="bgEl"
        src="https://images.unsplash.com/photo-1741991110666-88115e724741?fm=jpg&q=65&w=1600&auto=format&fit=crop"
        alt="Nairobi city skyline"
        fetchpriority="high"
      >
      <div class="rk-hero__scrim"></div>
      <div class="rk-hero__grain"></div>
    </div>

    <div class="rk-container rk-hero__grid">
      <!-- Copy -->
      <div class="rk-hero__copy">
        <div class="rk-kicker">
          <span class="rk-kicker__rule"></span>
          <span class="rk-kicker__text">
             <i class="bi bi-geo-alt"></i> Built for landlords &amp; agents in Kenya
             </span>
        </div>
        

        <h1 class="rk-hero__title">
          Every unit accounted for.<br>
          Every tenant <em>informed</em>.
        </h1>

        <p class="rk-hero__desc">
          Rental Kenya replaces the spreadsheet and the WhatsApp group with one
          dashboard — tenant records, rent tracking, and automatic SMS reminders,
          all in one place.
        </p>

        <div class="rk-hero__cta">
          <button class="rk-btn rk-btn--primary" @click="$router.push('/book-demo')">
            <span>Book a demo</span>
            <i class="bi bi-arrow-right"></i>
          </button>
          <button class="rk-btn rk-btn--secondary" @click="watchDemo">
            <i class="bi bi-play-circle-fill"></i>
            <span>See how it works</span>
          </button>
        </div>

        <div class="rk-hero__stats">
          <div class="rk-stat">
            <span class="rk-stat__value">500+</span>
            <span class="rk-stat__label">Landlords &amp; agents</span>
          </div>
          <div class="rk-stat">
            <span class="rk-stat__value">5,000+</span>
            <span class="rk-stat__label">Units tracked</span>
          </div>
          <div class="rk-stat">
            <span class="rk-stat__value">4.3<small>/5</small></span>
            <span class="rk-stat__label">Average rating</span>
          </div>
        </div>
      </div>

      <!-- Signature: Live Rent Roll card -->
      <div class="rk-hero__visual">
        
        <div class="rk-ledger">
          <div class="rk-ledger__head">
            <div>
              <span class="rk-ledger__title">Rent Roll</span>
              <span class="rk-ledger__sub">October · Pacific Crest</span>
            </div>
            <span class="rk-ledger__badge"><i class="bi bi-circle-fill"></i> Live</span>
          </div>

          <div class="rk-ledger__rows">
            <div
              class="rk-ledger__row"
              v-for="(row, i) in rentRoll"
              :key="row.unit"
              :style="{ animationDelay: `${i * 0.12}s` }"
            >
              <div class="rk-ledger__unit">
                <span class="rk-ledger__code">{{ row.unit }}</span>
                <span class="rk-ledger__tenant">{{ row.tenant }}</span>
              </div>
              <span class="rk-ledger__amount">KES {{ row.amount }}</span>
              <span class="rk-ledger__status" :class="`is-${row.status}`">
                <i :class="statusIcon(row.status)"></i>
                {{ statusLabel(row.status) }}
              </span>
            </div>
          </div>

          <div class="rk-ledger__foot">
            <i class="bi bi-chat-dots"></i>
            <span>Auto-reminder sent via SMS · 2 days ago</span>
          </div>
        </div>

        <div class="rk-hero__chip rk-hero__chip--top">
          <i class="bi bi-graph-up-arrow"></i>
          <div>
            <strong>96%</strong>
            <small>Collection rate</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Diagonal break into next section -->
    <svg class="rk-hero__edge" viewBox="0 0 1440 90" preserveAspectRatio="none">
      <path d="M0,90 L1440,20 L1440,90 Z" fill="var(--paper)"></path>
    </svg>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'


const bgEl = ref(null)
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
let ticking = false
const onScroll = () => {
  if (reducedMotion || ticking) return
  ticking = true
  requestAnimationFrame(() => {
    if (bgEl.value) {
      const y = window.scrollY
      bgEl.value.style.transform = `translateY(${y * 0.25}px) scale(1.08)`
    }
    ticking = false
  })
}
onMounted(() => window.addEventListener('scroll', onScroll, { passive: true }))
onUnmounted(() => window.removeEventListener('scroll', onScroll))

const rentRoll = [
  { unit: 'A1', tenant: 'J. Mwangi', amount: '15,000', status: 'paid' },
  { unit: 'B2', tenant: 'F. Wanjiru', amount: '12,000', status: 'due' },
  { unit: 'C3', tenant: 'D. Otieno', amount: '10,000', status: 'overdue' },
  { unit: 'D4', tenant: 'M. Achieng', amount: '18,000', status: 'paid' },
]

const statusIcon = (status) => ({
  paid: 'bi bi-check-circle-fill',
  due: 'bi bi-hourglass-split',
  overdue: 'bi bi-exclamation-circle-fill',
}[status])

const statusLabel = (status) => ({
  paid: 'Paid',
  due: 'Due',
  overdue: 'Overdue',
}[status])

//const bookDemo = () => { /* wire up to router or modal */ }
//const watchDemo = () => { /* wire up to router or modal */ }
</script>

<style scoped>
.rk-hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 9rem 0 7rem;
  overflow: hidden;
  isolation: isolate;
}

/* Photo background */
.rk-hero__bg { position: absolute; inset: 0; z-index: -2; }
.rk-hero__bg img { width: 100%; height: 100%; object-fit: cover; }
.rk-hero__scrim {
  position: absolute; inset: 0;
  background:
    linear-gradient(100deg, rgba(16,27,45,0.96) 0%, rgba(16,27,45,0.88) 38%, rgba(16,27,45,0.55) 65%, rgba(16,27,45,0.4) 100%),
    linear-gradient(180deg, rgba(16,27,45,0.2) 0%, rgba(16,27,45,0.75) 100%);
}
.rk-hero__grain {
  position: absolute; inset: 0; opacity: 0.35; mix-blend-mode: overlay; pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.4'/%3E%3C/svg%3E");
}

.rk-container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; width: 100%; }

.rk-hero__grid {
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 2;
}

/* Copy */
.rk-kicker {
  display: inline-flex; align-items: center; gap: 0.85rem;
  margin-bottom: 1rem;
}
.rk-kicker__rule { width: 32px; height: 3px; background: var(--brass); border-radius: 2px; flex-shrink: 0; }
.rk-kicker__text {
  display: inline-flex; align-items: center; gap: 0.5rem;
  font-family: var(--font-mono); font-size: 0.95rem; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase; color: var(--brass);
}
.rk-kicker__text i { font-size: 1rem; }
.rk-section__head h2 {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: 2.6rem;
  letter-spacing: -0.01em;
  color: var(--ink);
  line-height: 1.15;
}

.rk-section__head {
  max-width: 640px;
  margin-bottom: 80px;
}

.rk-hero__title {
  font-family: var(--font-display);
  font-size: 3.9rem;
  font-weight: 400;
  line-height: 1.06;
  color: var(--paper);
  letter-spacing: -0.01em;
  margin: 0 0 1.5rem;
  text-wrap: balance;
}
.rk-hero__title em { font-style: italic; color: var(--brass-light); }

.rk-hero__desc {
  font-family: var(--font-body); font-size: 1.18rem; line-height: 1.7;
  color: rgba(247,246,242,0.78); max-width: 480px; margin: 0 0 2.25rem;
}

.rk-hero__cta { display: flex; gap: 1rem; margin-bottom: 3.25rem; flex-wrap: wrap; }

.rk-btn {
  display: inline-flex; align-items: center; gap: 0.65rem;
  font-family: var(--font-body); font-weight: 600; font-size: 1rem;
  border-radius: var(--radius-sm); border: none; cursor: pointer;
  padding: 0.95rem 1.75rem; transition: all 0.25s var(--ease);
}
.rk-btn--primary { background: var(--brass); color: var(--ink); }
.rk-btn--primary:hover { background: var(--brass-light); transform: translateY(-2px); box-shadow: 0 12px 28px -8px rgba(176,141,63,0.5); }
.rk-btn--secondary { background: rgba(255,255,255,0.06); color: var(--paper); border: 1.5px solid rgba(255,255,255,0.25); backdrop-filter: blur(6px); }
.rk-btn--secondary:hover { border-color: var(--brass-light); background: rgba(255,255,255,0.1); }
.rk-btn--secondary i { color: var(--brass-light); font-size: 1.1rem; }

.rk-hero__stats { display: flex; gap: 2.5rem; flex-wrap: wrap; }
.rk-stat { display: flex; flex-direction: column; gap: 0.15rem; }
.rk-stat__value { font-family: var(--font-mono); font-size: 1.65rem; font-weight: 600; color: var(--paper); }
.rk-stat__value small { font-size: 1rem; color: rgba(247,246,242,0.5); }
.rk-stat__label { font-family: var(--font-body); font-size: 0.85rem; color: rgba(247,246,242,0.55); }

/* Ledger card (signature element) */
.rk-hero__visual { position: relative; }

.rk-hero__scene {
  position: absolute; inset: -15% -20%; z-index: 1;
  opacity: 0.85; pointer-events: none;
}

.rk-ledger {
  background: rgba(16,27,45,0.72);
  backdrop-filter: blur(18px);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: var(--radius-lg);
  padding: 1.75rem;
  box-shadow: 0 30px 60px -20px rgba(0,0,0,0.5);
  position: relative;
  z-index: 2;
}

.rk-ledger__head { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 1.5rem; }
.rk-ledger__title { display: block; font-family: var(--font-display); font-size: 1.4rem; color: var(--paper); font-style: italic; }
.rk-ledger__sub { display: block; font-family: var(--font-mono); font-size: 0.75rem; color: rgba(247,246,242,0.5); margin-top: 0.2rem; }
.rk-ledger__badge {
  display: inline-flex; align-items: center; gap: 0.4rem;
  font-family: var(--font-mono); font-size: 0.7rem; color: var(--sage);
  background: rgba(79,122,107,0.22); padding: 0.35rem 0.7rem; border-radius: 100px;
}
.rk-ledger__badge i { font-size: 0.5rem; animation: pulse 2s infinite; }

.rk-ledger__rows { display: flex; flex-direction: column; gap: 0.6rem; }
.rk-ledger__row {
  display: grid; grid-template-columns: 1fr auto auto;
  align-items: center; gap: 1rem;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: var(--radius-sm);
  padding: 0.85rem 1rem;
  opacity: 0; animation: rowIn 0.5s var(--ease) forwards;
}

.rk-ledger__unit { display: flex; flex-direction: column; gap: 0.1rem; }
.rk-ledger__code { font-family: var(--font-mono); font-size: 0.8rem; color: var(--brass-light); font-weight: 600; }
.rk-ledger__tenant { font-family: var(--font-body); font-size: 0.88rem; color: var(--paper); }
.rk-ledger__amount { font-family: var(--font-mono); font-size: 0.85rem; color: rgba(247,246,242,0.75); }

.rk-ledger__status {
  display: inline-flex; align-items: center; gap: 0.35rem;
  font-family: var(--font-mono); font-size: 0.72rem; font-weight: 500;
  padding: 0.3rem 0.6rem; border-radius: 100px; white-space: nowrap;
}
.rk-ledger__status.is-paid { color: #7FBFA8; background: rgba(79,122,107,0.22); }
.rk-ledger__status.is-due { color: #E0B15C; background: rgba(192,138,46,0.22); }
.rk-ledger__status.is-overdue { color: #E08363; background: rgba(196,86,47,0.22); }

.rk-ledger__foot {
  display: flex; align-items: center; gap: 0.6rem; margin-top: 1.25rem;
  padding-top: 1.1rem; border-top: 1px solid rgba(255,255,255,0.1);
  font-family: var(--font-body); font-size: 0.8rem; color: rgba(247,246,242,0.55);
}
.rk-ledger__foot i { color: var(--brass-light); }

.rk-hero__chip {
  position: absolute; z-index: 3;
  display: flex; align-items: center; gap: 0.75rem;
  background: rgba(255,255,255,0.95); border: 1px solid var(--line);
  border-radius: var(--radius-md); padding: 0.9rem 1.1rem;
  box-shadow: 0 16px 32px -12px rgba(0,0,0,0.35);
}
.rk-hero__chip--top { top: -1.5rem; left: -1.5rem; }
.rk-hero__chip i { font-size: 1.3rem; color: var(--sage); }
.rk-hero__chip strong { display: block; font-family: var(--font-mono); font-size: 1.05rem; color: var(--ink); }
.rk-hero__chip small { font-family: var(--font-body); font-size: 0.72rem; color: var(--slate-soft); }

.rk-hero__edge { position: absolute; bottom: -1px; left: 0; width: 100%; height: 90px; z-index: 3; }

@keyframes rowIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

@media (prefers-reduced-motion: reduce) {
  .rk-ledger__row { animation: none; opacity: 1; }
  .rk-ledger__badge i { animation: none; }
}

@media (max-width: 1024px) {
  .rk-hero__title { font-size: 3.1rem; }
  .rk-hero__grid { gap: 3rem; }
}

@media (max-width: 860px) {
  .rk-hero { padding: 7.5rem 0 6rem; }
  .rk-hero__grid { grid-template-columns: 1fr; }
  .rk-hero__visual { order: -1; max-width: 460px; margin: 0 auto; }
  .rk-hero__chip--top { top: -1rem; left: -1rem; }
  .rk-hero__title { font-size: 2.5rem; }
  .rk-hero__desc { max-width: none; }
}

@media (max-width: 480px) {
  .rk-hero__title { font-size: 2.05rem; }
  .rk-ledger__row { grid-template-columns: 1fr auto; grid-template-areas: "unit status" "amount amount"; }
  .rk-ledger__amount { grid-area: amount; padding-top: 0.3rem; }
  .rk-hero__stats { gap: 1.5rem; }
}
</style>
